import { ChangeEvent, ElementRef, FC, useEffect, useRef, useState } from 'react';
import { AddressSuggestions, DaDataSuggestion, DaDataAddress } from 'react-dadata';
import 'react-dadata/dist/react-dadata.css';
import './react-address-dadata.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { formatOrderAddress } from 'utils/strActions';
import { ReactComponent as IconLocation } from './img/iconLocation.svg';

const hasSuggestionsPayload = (responseText: string) => {
  try {
    return Array.isArray(JSON.parse(responseText)?.suggestions);
  } catch {
    return false;
  }
};

interface AddressDadata2Props {
  initialAddress?: {
    city?: string;
    street?: string;
    home?: string;
    apartment?: string;
  };
  placeholder?: string;
  error?: boolean;
}

export const AddressDadata2: FC<AddressDadata2Props> = ({
  initialAddress,
  placeholder = 'Укажи адрес',
  error = false,
}) => {
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const [value, setValue] = useState<DaDataSuggestion<DaDataAddress> | null>(null);
  const [defaultValue, setDefaultValue] = useState<string | undefined>(undefined);
  const suggestionsRef = useRef<ElementRef<typeof AddressSuggestions>>(null);
  const typedTextRef = useRef<string | null>(null);

  const dispatch = useDispatch();

  const handleChange = (suggestion?: DaDataSuggestion<DaDataAddress>) => {
    typedTextRef.current = null;
    setValue(suggestion ?? null);
  };

  useEffect(() => {
    const originalOpen = XMLHttpRequest.prototype.open;
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const setAvailable = (available: boolean) =>
      dispatch(setInfoOrderDetail({ key: 'is_dadata_available', value: available }));

    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      if (String(url).includes('suggestions.dadata.ru')) {
        const timer = setTimeout(() => {
          timers.delete(timer);
          setAvailable(false);
        }, 3000);
        timers.add(timer);

        const stopTimer = () => {
          clearTimeout(timer);
          timers.delete(timer);
        };

        let aborted = false;
        this.addEventListener('abort', () => {
          aborted = true;
          stopTimer();
        });

        this.addEventListener('loadend', () => {
          if (aborted) return;
          stopTimer();
          setAvailable(this.status === 200 && hasSuggestionsPayload(this.responseText));
        });
      }
      return (originalOpen as (...args: unknown[]) => void).call(
        this,
        method,
        url,
        ...rest,
      );
    } as typeof XMLHttpRequest.prototype.open;

    return () => {
      timers.forEach(clearTimeout);
      XMLHttpRequest.prototype.open = originalOpen;
    };
  }, []);

  useEffect(() => {
    if (
      initialAddress &&
      (initialAddress.city || initialAddress.street || initialAddress.home)
    ) {
      const formattedAddress = formatOrderAddress({
        city: initialAddress.city,
        street: initialAddress.street,
        home: initialAddress.home,
        apartment: initialAddress.apartment,
      });

      if (suggestionsRef.current && formattedAddress) {
        suggestionsRef.current.setInputValue(formattedAddress);
      }
    }
  }, [
    initialAddress?.city,
    initialAddress?.street,
    initialAddress?.home,
    initialAddress?.apartment,
  ]);

  useEffect(() => {
    if (
      defaultValue === undefined &&
      infoOrderDetail.address &&
      !infoOrderDetail.city &&
      !infoOrderDetail.street &&
      !infoOrderDetail.home &&
      suggestionsRef.current
    ) {
      suggestionsRef.current.setInputValue(infoOrderDetail.address);
    }
  }, [infoOrderDetail.address]);

  useEffect(() => {
    const suggestionData = value?.data;

    const fullAddress = [
      suggestionData?.region_with_type,
      suggestionData?.area_with_type,
      suggestionData?.city_with_type,
      suggestionData?.settlement_with_type,
      suggestionData?.street_with_type,
      suggestionData?.house &&
        [
          suggestionData.house_type,
          suggestionData.house,
          suggestionData.block && suggestionData.block_type,
          suggestionData.block,
        ]
          .filter(Boolean)
          .join(' '),
      suggestionData?.flat && `${suggestionData.flat_type} ${suggestionData.flat}`,
    ]
      .filter((part, index, parts) => part && parts.indexOf(part) === index)
      .join(', ');

    if (suggestionData) {
      suggestionData.flat !== null
        ? dispatch(setInfoOrderDetail({ key: 'apartment', value: suggestionData.flat }))
        : dispatch(setInfoOrderDetail({ key: 'apartment', value: '' }));
      suggestionData.city !== null
        ? dispatch(setInfoOrderDetail({ key: 'city', value: suggestionData.city }))
        : dispatch(setInfoOrderDetail({ key: 'city', value: '' }));
      suggestionData.street_with_type !== null
        ? dispatch(
            setInfoOrderDetail({ key: 'street', value: suggestionData.street_with_type }),
          )
        : dispatch(setInfoOrderDetail({ key: 'street', value: '' }));
      suggestionData.house !== null
        ? dispatch(setInfoOrderDetail({ key: 'home', value: suggestionData.house }))
        : dispatch(setInfoOrderDetail({ key: 'home', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'address', value: fullAddress }));
    }

    if (!infoOrderDetail.is_dadata_available && defaultValue !== undefined) {
      dispatch(setInfoOrderDetail({ key: 'address', value: defaultValue }));
    }

    if (defaultValue === '' && suggestionsRef.current) {
      suggestionsRef.current.setInputValue('');
      setValue(null);
      dispatch(setInfoOrderDetail({ key: 'apartment', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'city', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'street', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'home', value: '' }));
      dispatch(setInfoOrderDetail({ key: 'address', value: '' }));
    }
  }, [value, defaultValue, infoOrderDetail.is_dadata_available]);

  return (
    <div
      style={{ position: 'relative' }}
      className={error ? 'address-dadata-error' : undefined}>
      <AddressSuggestions
        ref={suggestionsRef}
        token={`${process.env.REACT_APP_API_KEY}`}
        value={value ?? undefined}
        onChange={handleChange}
        defaultQuery={formatOrderAddress(infoOrderDetail)}
        selectOnBlur={true}
        count={5}
        highlightClassName='suggestion_highlight'
        currentSuggestionClassName='suggestion_select'
        inputProps={{
          placeholder,
          style: { paddingRight: '35px' },
          onChange: (e: ChangeEvent<HTMLInputElement>) => {
            if (e) setDefaultValue(e.target.value.trimStart());
          },
        }}
      />
      <IconLocation className='address-dadata-location-icon' />
    </div>
  );
};
